import { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { className } from "@/common/utils/strings";
import { ITagListItem } from "./types";
import CloseIcon from "@/common/images/icons/x.svg?react";

import "./TagListButton.scss";
import { metricsBus } from "@/common/buses";
import { SearchInitiator, useAnalyticsStore } from "@/common/store/analyticsStore";
import { useSfw } from "@/common/hooks/useSfw";

interface ITagListButton {
  item: ITagListItem;
  visible: boolean;
  onWidth: (id: string, width: number) => void;
  onTagClick?: (name: string) => void;
  selectedTags?: string[];
  isFeedbackMode?: boolean;
  isIrrelevant?: boolean;
  onSendTagFeedback?: (tag: string) => void;
}

export const TagListButton = (props: ITagListButton) => {
  const {
    selectedTags,
    item: { id, link, text },
    onWidth,
    onTagClick,
    visible,
    isIrrelevant,
    isFeedbackMode,
    onSendTagFeedback
  } = props;

  const { sanitizeImage, isLoading } = useSfw();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const tagClasses = className({
    "tagButton": true,
    "visible": visible,
    "hidden": !visible,
    "niche": id.indexOf("niche:") >= 0,
    "tag": id.indexOf("tag:") >= 0,
    "selected": !!selectedTags?.includes(text)
  });
  const thumbnail = props.item.thumbnail ? sanitizeImage(props.item.thumbnail) : null;

  const setSearchInitiator = useAnalyticsStore((state) => state.setSearchInitiator);

  const handleTagLinkClick = useCallback(() => {
    setSearchInitiator(SearchInitiator.TagLink);
    sendMetrics(thumbnail ? "Niche" : "Tag", link);
  }, [props.item, link]);

  const handleTagClick = () => {
    metricsBus.emit("tagClicked", { tag: props.item.text });

    if (onTagClick) {
      onTagClick(text);
    }
  };

  const handleSendTagFeedback = () => {
    if (onSendTagFeedback) {
      onSendTagFeedback(props.item.text);
    }
  };

  useEffect(() => {
    if (onTagClick && buttonRef.current) {
      onWidth(id, buttonRef.current.offsetWidth);
    }

    if (!onTagClick && linkRef.current) {
      onWidth(id, linkRef.current.offsetWidth);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, buttonRef.current?.offsetWidth, linkRef.current?.offsetWidth]);

  const sendMetrics = (elementType: string, targetURL: string) => {
    metricsBus.emit("watchPageNavigation", { elementType, targetURL });

    if (elementType === "Niche") {
      metricsBus.emit("playerNicheClicked");
    }

    if (elementType === "Tag") {
      metricsBus.emit("playerTagClicked");
    }
  };

  const renderButtonTag = () => {
    return (
      <button onClick={handleTagClick} className={tagClasses} ref={buttonRef} aria-label={`${text} tag`}>
        <span className="text">{text}</span>
      </button>
    );
  };

  const renderFeedbackTag = () => {
    if (thumbnail) {
      return renderLinkTag();
    }

    return (
      <button onClick={handleSendTagFeedback} className={`${tagClasses} tagButtonFeedback ${isIrrelevant ? "isIrrelevant" : ""}`}>
        <span className="text">{text}</span>
        <CloseIcon />
      </button>
    );
  };

  const renderLinkTag = () => {
    return (
      <Link to={link} onClick={handleTagLinkClick} className={tagClasses} ref={linkRef}>
        <div className="tagContainer">
          {thumbnail && (
            <img className="icon" loading="lazy" src={thumbnail} width="24" height="24" alt={`Poster for ${text} tag`} />
          )}
          <span className="text">{text}</span>
        </div>
      </Link>
    );
  };

  const renderTag = () => {
    if (onTagClick) {
      return renderButtonTag();
    }

    if (isFeedbackMode) {
      return renderFeedbackTag();
    }

    return renderLinkTag();
  };

  return !isLoading ? (
    <>
      {renderTag()}
    </>
  ) : null;
};
